// src/pages/Dashboard.jsx - FOCUSED REVENUE METRICS VERSION
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import AddProduct from '../components/productManagement/AddProduct';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL;
const APP_VERSION = "V1.0.25";

// Climate impact calculations
const calculateEnvironmentalEquivalents = (co2Kg) => {
    if (!co2Kg || co2Kg <= 0) return { milesDriven: 0, treesPlanted: 0 };

    const milesDriven = Math.round((co2Kg / 0.404) * 10) / 10;
    const treesPlanted = Math.round((co2Kg / 21.77) * 10) / 10;

    return { milesDriven, treesPlanted };
};

// Utility functions
const formatMetricValue = (value, fallback = '0') => {
    if (value === null || value === undefined || value === '...') return fallback;
    if (typeof value === 'number') {
        if (value < 0.01 && value > 0) return value.toFixed(4);
        if (value >= 1000) return Math.round(value).toLocaleString();
        return Math.round(value * 100) / 100;
    }
    if (typeof value === 'string' && !isNaN(parseFloat(value))) {
        const numValue = parseFloat(value);
        return formatMetricValue(numValue, fallback);
    }
    return fallback;
};

const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '...') return "KShs. 0";
    const numericAmount = typeof amount === 'number' ? amount : parseFloat(amount);

    if (isNaN(numericAmount) || numericAmount === 0) return "KShs. 0";

    // Match the same formatting logic as MatchPage
    if (numericAmount < 1) return `KShs. ${numericAmount.toFixed(4)}`;
    if (numericAmount < 1000) return `KShs. ${numericAmount.toFixed(2)}`;

    return `KShs. ${numericAmount.toLocaleString('en-KE', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    })}`;
};

// Calculate waste to emissions conversion
const calculateEmissionsFromWaste = (wasteKg, productType = 'general') => {
    const emissionFactors = {
        'fertiliser': 3.2,
        'pesticide': 4.1,
        'herbicide': 3.8,
        'seedlings': 1.2,
        'fruits': 0.8,
        'vegetables': 0.7,
        'cereals': 1.1,
        'legumes': 0.9,
        'general': 2.5
    };

    const factor = emissionFactors[productType.toLowerCase()] || emissionFactors.general;
    return wasteKg * factor;
};

// RecommendationCard component
const RecommendationCard = ({ message, linkTo, actionText, icon: Icon }) => {
    return (
        <div className="bg-neutral border-l-4 border-primary rounded-lg p-3 sm:p-4 flex flex-col md:flex-row xs:items-center justify-between gap-3 shadow-md mb-4 sm:mb-6 transition duration-300 hover:shadow-lg">
            <div className="flex items-center min-w-0 flex-1">
                <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mr-2 sm:mr-3" />
                <p className="text-xs font-medium text-gray-800 break-words line-clamp-2 xs:line-clamp-1">
                    {message}
                </p>
            </div>
            <Link to={linkTo} className="flex-shrink-0 xs:w-auto">
                <button className="bg-primary hover:bg-primary-light text-white text-xs font-semibold py-2 xs:py-1.5 px-4 rounded-full transition-colors whitespace-nowrap w-full xs:w-auto text-center font-accent">
                    {actionText}
                </button>
            </Link>
        </div>
    );
};

// Icons
const BoltIcon = (props) => (
    <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
);

const UserGroupIcon = (props) => (
    <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.65-.183-1.274-.543-1.857A9.953 9.953 0 0012 11.25a9.953 9.953 0 00-4.457 2.893C7.183 17.181 7 17.65 7 18v2H2M7 20h2V18c0-.65-.183-1.274-.543-1.857M14 12a2 2 0 100-4 2 2 0 000 4z" />
    </svg>
);

const ClockIcon = (props) => (
    <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const ChartBarIcon = (props) => (
    <svg {...props} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
);

// Enhanced Metric Card Component with focused revenue display
const EnhancedMetricCard = ({
    title,
    mainValue,
    subtitle,
    subvalue,
    additionalInfo,
    borderColor = 'border-gray-200',
    textColor = 'text-gray-800',
    climateContext
}) => {
    return (
        <div className={`p-4 bg-white border ${borderColor} rounded-xl shadow-sm hover:shadow-md transition-all duration-200 h-full flex flex-col`}>
            <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 font-subheading">{title}</h3>
                <div className="mb-3">
                    <p className={`text-xl font-bold ${textColor} mb-1 font-heading`}>
                        {mainValue}
                    </p>
                    {climateContext && (
                        <p className="text-xs text-gray-500 mt-1 italic">
                            {climateContext}
                        </p>
                    )}
                </div>

                {(subtitle || subvalue) && (
                    <div className="bg-neutral border border-primary-light rounded-lg p-3 mb-3">
                        <div className="flex items-center justify-between">
                            {subtitle && (
                                <span className="text-xs text-gray-600 font-medium">{subtitle}</span>
                            )}
                            {subvalue && (
                                <span className="text-sm font-semibold text-primary">{subvalue}</span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {additionalInfo && (
                <div className="border-t border-gray-100 pt-3 mt-3">
                    <div className="space-y-2 text-xs text-gray-600">
                        {additionalInfo}
                    </div>
                </div>
            )}
        </div>
    );
};

const Dashboard = () => {
    const { user, token, isAuthenticated, loading: authLoading } = useAuth();
    const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
    const [loadingMetrics, setLoadingMetrics] = useState(true);

    // State for AI Recommendations
    const [aiRecommendations, setAiRecommendations] = useState([]);

    // FOCUSED Revenue Metrics State
    const [impactMetrics, setImpactMetrics] = useState({
        // REVENUE METRICS - FOCUSED CHANGES
        revenueGenerated: '...', // Sum of offeredPrice for all delivered products
        pendingRevenue: '...',   // Sum of offeredPrice for all pending products

        // Environmental metrics
        actualWastePreventedKg: '...',
        actualPollutionPreventedCo2: '...',
        potentialWastePreventedKg: '...',
        potentialPollutionPreventedCo2: '...',
        potentialRevenue: '...',

        // Listing counts
        totalActiveListings: '...',
        totalInventoryValue: '...',
        expiringSoonCount: '...',
        matchedListingsCount: '...',
        totalListingsCount: '...',
        totalDeliveredMatches: '...',
        totalPendingMatches: '...'
    });

    const displayFirstName = user?.firstName || 'Farmer';
    const displayBusinessName = user?.business || 'Your Farm';

    const handleOpenRequestModal = () => setIsRequestModalOpen(true);
    const handleCloseRequestModal = () => setIsRequestModalOpen(false);

    // Calculate environmental equivalents for display
    const actualEquivalents = calculateEnvironmentalEquivalents(impactMetrics.actualPollutionPreventedCo2);
    const potentialEquivalents = calculateEnvironmentalEquivalents(impactMetrics.potentialPollutionPreventedCo2);

    // Convert quantity to weight in kg
    const convertQuantityToWeight = (quantity, unit, baseUnit) => {
        const weightConversions = {
            'kg': 1,
            'g': 0.001,
            'ton': 1000,
            'lbs': 0.453592,
            'oz': 0.0283495,
            'liter': 1,
            'ml': 0.001,
            'gallon': 3.78541,
            'bushels': 27.2155,
            'bags': 25,
            'packets': 0.1,
            'sacks': 50,
            'bales': 500,
            'units': 0.1,
            'pieces': 0.1,
            'crates': 10,
            'dozens': 1.2,
            'bunches': 0.5
        };

        const conversionFactor = weightConversions[unit] || weightConversions[baseUnit] || 1;
        return quantity * conversionFactor;
    };

    // FOCUSED: Calculate revenue based on offered prices only (not multiplied by quantity)
    const calculateMatchRevenue = (matches, statusFilter = []) => {
        return matches.reduce((total, match) => {
            if (statusFilter.length === 0 || statusFilter.includes(match.status)) {
                // USE ONLY THE OFFERED PRICE (not multiplied by quantity)
                const matchOfferedPrice = parseFloat(match.offeredPrice) || parseFloat(match.price) || 0;
                
                console.log(`💰 Match Revenue Calculation:`, {
                    matchId: match._id,
                    status: match.status,
                    offeredPrice: match.offeredPrice,
                    price: match.price,
                    usedPrice: matchOfferedPrice,
                    revenue: matchOfferedPrice
                });
                
                return total + matchOfferedPrice;
            }
            return total;
        }, 0);
    };

    // FOCUSED: Enhanced Impact Metrics Fetching with CORRECTED revenue focus
    useEffect(() => {
        const fetchEnhancedImpactMetrics = async () => {
            if (authLoading || !isAuthenticated || !token) {
                return;
            }

            setLoadingMetrics(true);

            try {
                const productsResponse = await fetch(`${API_URL}/api/products/my-products`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!productsResponse.ok) {
                    throw new Error(`Failed to fetch products: ${productsResponse.status}`);
                }

                const products = await productsResponse.json();

                // FOCUSED: Initialize revenue-specific metrics
                let totalDeliveredRevenue = 0;    // Sum of offeredPrice for delivered products
                let totalPendingRevenue = 0;      // Sum of offeredPrice for pending products
                let totalPotentialRevenue = 0;    // Total value of active listings (offeredPrice for unexpired products)
                let totalInventoryValue = 0;      // Total value of all products (offeredPrice including expired)

                // Environmental metrics
                let totalPotentialWeightKg = 0;
                let deliveredWeightKg = 0;
                let pendingWeightKg = 0;

                // Count metrics
                let expiringSoonCount = 0;
                let matchedListingsCount = 0;
                let totalActiveListings = 0;
                let totalPendingMatches = 0;
                let totalDeliveredMatches = 0;

                const now = new Date();
                const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

                // Data for AI recommendations
                let expiringProducts = [];
                let productsWithPendingMatches = [];
                let productsWithDeliveredMatches = [];
                let productsWithoutMatches = [];
                let lowInventoryProducts = [];

                // FOCUSED: CORRECTED - Process each product with revenue calculation as primary focus
                products.forEach(product => {
                    const isExpired = new Date(product.expiry) < now;
                    const isActive = !product.sold && !isExpired && product.status !== 'expired';

                    if (isActive) {
                        totalActiveListings++;
                    }

                    // FOCUSED: CORRECTED - Calculate product values using OFFERED PRICE
                    const availableQuantity = parseFloat(product.quantity) || 0;
                    const offeredPrice = parseFloat(product.offeredPrice) || parseFloat(product.price) || 0;
                    
                    // Calculate product value based on offered price (not multiplied by quantity)
                    const productValue = offeredPrice;

                    // ACTIVE LISTING VALUE: Sum of offeredPrice for unexpired products
                    if (isActive) {
                        totalPotentialRevenue += productValue;
                    }

                    // TOTAL INVENTORY VALUE: Sum of offeredPrice for ALL products (including expired)
                    totalInventoryValue += productValue;

                    // Environmental calculations (keep as is)
                    if (isActive) {
                        const productWeightKg = convertQuantityToWeight(availableQuantity, product.unit, product.baseUnit);
                        totalPotentialWeightKg += productWeightKg;
                    }

                    // Check if product is expiring soon
                    if (product.expiry && new Date(product.expiry) <= thirtyDaysFromNow && isActive) {
                        expiringSoonCount++;
                        expiringProducts.push(product);
                    }

                    // Check for low inventory
                    if (availableQuantity < 10 && isActive) {
                        lowInventoryProducts.push(product);
                    }

                    // FOCUSED: CORRECTED - Process matches with revenue as primary calculation
                    let hasPendingMatches = false;
                    let hasDeliveredMatches = false;

                    if (product.matches && Array.isArray(product.matches) && product.matches.length > 0) {
                        matchedListingsCount++;

                        // FOCUSED: CORRECTED revenue calculations - USE ONLY OFFERED PRICE
                        // REVENUE GENERATED: Sum of offeredPrice for 'delivered' status matches
                        const productDeliveredRevenue = calculateMatchRevenue(product.matches, ['delivered']);

                        // PENDING REVENUE: Sum of offeredPrice for pending status matches
                        const productPendingRevenue = calculateMatchRevenue(product.matches, [
                            'pending', 'matched', 'accepted', 'processing', 'shipped'
                        ]);

                        totalDeliveredRevenue += productDeliveredRevenue;
                        totalPendingRevenue += productPendingRevenue;

                        console.log(`💰 Product "${product.name}" Revenue Summary:`, {
                            deliveredRevenue: productDeliveredRevenue,
                            pendingRevenue: productPendingRevenue,
                            totalDeliveredSoFar: totalDeliveredRevenue,
                            totalPendingSoFar: totalPendingRevenue
                        });

                        // Calculate weights for environmental impact
                        const productDeliveredWeight = product.matches.reduce((total, match) => {
                            if (match.status === 'delivered') {
                                const matchQuantity = parseFloat(match.quantity) || 0;
                                return total + convertQuantityToWeight(matchQuantity, product.unit, product.baseUnit);
                            }
                            return total;
                        }, 0);

                        const productPendingWeight = product.matches.reduce((total, match) => {
                            const pendingStatuses = ['pending', 'matched', 'accepted', 'processing', 'shipped'];
                            if (pendingStatuses.includes(match.status)) {
                                const matchQuantity = parseFloat(match.quantity) || 0;
                                return total + convertQuantityToWeight(matchQuantity, product.unit, product.baseUnit);
                            }
                            return total;
                        }, 0);

                        deliveredWeightKg += productDeliveredWeight;
                        pendingWeightKg += productPendingWeight;

                        // Track match counts
                        const deliveredMatches = product.matches.filter(m => m.status === 'delivered');
                        const pendingMatches = product.matches.filter(m =>
                            ['pending', 'matched', 'accepted', 'processing', 'shipped'].includes(m.status)
                        );

                        hasDeliveredMatches = deliveredMatches.length > 0;
                        hasPendingMatches = pendingMatches.length > 0;
                        totalDeliveredMatches += deliveredMatches.length;
                        totalPendingMatches += pendingMatches.length;

                        if (hasPendingMatches) {
                            productsWithPendingMatches.push({
                                ...product,
                                pendingMatchesCount: pendingMatches.length,
                                pendingRevenue: productPendingRevenue
                            });
                        }
                        if (hasDeliveredMatches) {
                            productsWithDeliveredMatches.push({
                                ...product,
                                deliveredMatchesCount: deliveredMatches.length,
                                deliveredRevenue: productDeliveredRevenue
                            });
                        }
                    } else {
                        if (isActive) {
                            productsWithoutMatches.push(product);
                        }
                    }
                });

                // Calculate environmental metrics
                const actualWastePreventedKg = Math.round(deliveredWeightKg);
                const actualPollutionPreventedCo2 = Math.round(
                    calculateEmissionsFromWaste(actualWastePreventedKg)
                );

                const potentialWastePreventedKg = Math.round(totalPotentialWeightKg);
                const potentialPollutionPreventedCo2 = Math.round(
                    calculateEmissionsFromWaste(potentialWastePreventedKg)
                );

                // FOCUSED: Debug logging for revenue metrics
                console.log('💰 CORRECTED REVENUE METRICS:', {
                    revenueGenerated: totalDeliveredRevenue,
                    pendingRevenue: totalPendingRevenue,
                    activeListingValue: totalPotentialRevenue,
                    totalInventoryValue: totalInventoryValue,
                    deliveredMatches: totalDeliveredMatches,
                    pendingMatches: totalPendingMatches,
                    description: 'Revenue Generated = Sum of offeredPrice for delivered products, Pending Revenue = Sum of offeredPrice for pending products, Active Listing Value = Sum of offeredPrice for active products, Total Inventory Value = Sum of offeredPrice for all products'
                });

                // Generate AI recommendations
                const realRecommendations = [];

                // Recommendation 1: Products with pending matches
                if (productsWithPendingMatches.length > 0) {
                    const productNames = productsWithPendingMatches.map(p => p.name).slice(0, 2).join(', ');
                    realRecommendations.push({
                        id: 1,
                        message: `You have ${totalPendingMatches} pending match${totalPendingMatches > 1 ? 'es' : ''} for ${productNames} worth ${formatCurrency(totalPendingRevenue)}. Review them now!`,
                        linkTo: "/products?filter=matches",
                        actionText: "Review Matches",
                        icon: UserGroupIcon,
                    });
                }

                // Recommendation 2: Products expiring soon
                if (expiringProducts.length > 0) {
                    const productNames = expiringProducts.map(p => p.name).slice(0, 2).join(', ');
                    const daysUntilExpiry = expiringProducts[0].expiry ?
                        Math.ceil((new Date(expiringProducts[0].expiry) - now) / (1000 * 60 * 60 * 24)) : 0;

                    realRecommendations.push({
                        id: 2,
                        message: `Your ${productNames} ${expiringProducts.length > 1 ? 'are' : 'is'} expiring in ${daysUntilExpiry} days. Consider updating prices.`,
                        linkTo: "/products?filter=expiring",
                        actionText: "Check Listings",
                        icon: ClockIcon,
                    });
                }

                // Recommendation 3: Products without matches
                if (productsWithoutMatches.length > 0 && realRecommendations.length < 3) {
                    const productNames = productsWithoutMatches.map(p => p.name).slice(0, 2).join(', ');
                    realRecommendations.push({
                        id: 3,
                        message: `Boost visibility for ${productNames} by updating descriptions or adjusting prices.`,
                        linkTo: "/products",
                        actionText: "Optimize Listings",
                        icon: BoltIcon,
                    });
                }

                // Recommendation 4: Low inventory products
                if (lowInventoryProducts.length > 0 && realRecommendations.length < 3) {
                    const productNames = lowInventoryProducts.map(p => p.name).slice(0, 2).join(', ');
                    realRecommendations.push({
                        id: 4,
                        message: `Your ${productNames} ${lowInventoryProducts.length > 1 ? 'are' : 'is'} running low or out of stock. Consider restocking soon.`,
                        linkTo: "/add-inventory",
                        actionText: "Add Inventory",
                        icon: ChartBarIcon,
                    });
                }

                // Recommendation 5: Delivered products (successful transactions)
                if (productsWithDeliveredMatches.length > 0 && realRecommendations.length < 3) {
                    const productNames = productsWithDeliveredMatches.map(p => p.name).slice(0, 2).join(', ');
                    realRecommendations.push({
                        id: 5,
                        message: `${productNames} generated ${formatCurrency(totalDeliveredRevenue)} from ${totalDeliveredMatches} completed deliver${totalDeliveredMatches > 1 ? 'ies' : ''}!`,
                        linkTo: "/add-inventory",
                        actionText: "Add Similar",
                        icon: UserGroupIcon,
                    });
                }

                // Fallback recommendation
                if (realRecommendations.length === 0) {
                    if (totalActiveListings === 0) {
                        realRecommendations.push({
                            id: 6,
                            message: "Start your climate journey: List your first product to begin tracking impact",
                            linkTo: "/add-inventory",
                            actionText: "List First Product",
                            icon: BoltIcon,
                        });
                    } else {
                        realRecommendations.push({
                            id: 7,
                            message: "Your listings are performing well! Consider adding more products to increase your impact.",
                            linkTo: "/add-inventory",
                            actionText: "Add More Products",
                            icon: BoltIcon,
                        });
                    }
                }

                // Set the final FOCUSED metrics
                const finalMetrics = {
                    // FOCUSED REVENUE METRICS - CORRECTED
                    revenueGenerated: totalDeliveredRevenue, // Sum of offeredPrice for delivered products
                    pendingRevenue: totalPendingRevenue,     // Sum of offeredPrice for pending products
                    
                    // Environmental metrics
                    actualWastePreventedKg,
                    actualPollutionPreventedCo2,
                    potentialWastePreventedKg,
                    potentialPollutionPreventedCo2,
                    potentialRevenue: totalPotentialRevenue, // Active Listing Value
                    
                    // Listing counts
                    totalActiveListings,
                    totalInventoryValue, // Total Inventory Value (all products)
                    expiringSoonCount,
                    matchedListingsCount,
                    totalListingsCount: products.length,
                    totalDeliveredMatches,
                    totalPendingMatches
                };

                console.log('Final CORRECTED metrics:', finalMetrics);
                setImpactMetrics(finalMetrics);
                setAiRecommendations(realRecommendations);

            } catch (error) {
                console.error('Error fetching product metrics:', error);
                // Set zero values on error
                setImpactMetrics({
                    revenueGenerated: 0,
                    pendingRevenue: 0,
                    actualWastePreventedKg: 0,
                    actualPollutionPreventedCo2: 0,
                    potentialWastePreventedKg: 0,
                    potentialPollutionPreventedCo2: 0,
                    potentialRevenue: 0,
                    totalActiveListings: 0,
                    totalInventoryValue: 0,
                    expiringSoonCount: 0,
                    matchedListingsCount: 0,
                    totalListingsCount: 0,
                    totalDeliveredMatches: 0,
                    totalPendingMatches: 0
                });
            } finally {
                setLoadingMetrics(false);
            }
        };

        fetchEnhancedImpactMetrics();
    }, [token, isAuthenticated, authLoading]);

    // Check if we have any meaningful impact data
    const hasImpactData = !loadingMetrics && (
        Number(impactMetrics.totalActiveListings) > 0 ||
        Number(impactMetrics.revenueGenerated) > 0 ||
        Number(impactMetrics.actualWastePreventedKg) > 0
    );

    return (
        <div className="min-h-screen bg-neutral py-4 sm:py-8 lg:py-16 px-3 sm:px-4 lg:px-8 font-sans">
            <div className="max-w-4xl mx-auto">
                {/* Welcome Section */}
                <div className="mb-6 sm:mb-8 lg:mb-10 px-2 sm:px-0">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 font-heading">
                        Hello, {displayFirstName}! 👋
                    </h1>
                    <p className="text-gray-600 mt-1 text-sm sm:text-base">
                        Welcome back to <span className="font-semibold text-primary">{displayBusinessName}</span>'s dashboard
                    </p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-2">
                        Turn your excess inventory into environmental impact and revenue
                    </p>
                </div>

                {/* Quick Actions Section */}
                


                {/* Quick Actions - Icon Grid Layout */}
<div className="mb-6 sm:mb-8">
  <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 sm:mb-6 font-heading px-2 sm:px-0">
    Quick Actions
  </h2>
  
  <div className="grid grid-cols-2 gap-3 sm:gap-4">
    {/* Add Product Action */}
    <button
      onClick={handleOpenRequestModal}
      className="group bg-white rounded-xl p-4 sm:p-5 border border-gray-200 shadow-sm hover:shadow-md hover:border-primary-light transition-all duration-200 text-center"
    >
      <div className="w-12 h-12 bg-primary-light rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-200">
        <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
      </div>
      <h3 className="font-semibold text-gray-800 text-sm mb-1 font-subheading">Add Product</h3>
      <p className="text-xs text-gray-600">New listing</p>
    </button>

    {/* View Products Action */}
    <Link to="/products" className="block group">
      <div className="bg-white rounded-xl p-4 sm:p-5 border border-gray-200 shadow-sm hover:shadow-md hover:border-accent transition-all duration-200 text-center h-full">
        <div className="w-12 h-12 bg-accent-light rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-200">
          <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        <h3 className="font-semibold text-gray-800 text-sm mb-1 font-subheading">View Products</h3>
        <p className="text-xs text-gray-600">Manage listings</p>
      </div>
    </Link>
  </div>
</div>

                {/* MISSION IMPACT METRICS SECTION */}
                <div className="px-2 sm:px-0">
                    <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 sm:mb-6 border-b pb-3 font-heading">
                        Your Climate Impact Dashboard
                    </h2>

                    {loadingMetrics ? (
                        // Loading State
                        <div className="p-6 sm:p-8 bg-white border-2 border-gray-200 rounded-xl text-center shadow-sm">
                            <div className="animate-pulse">
                                <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-4"></div>
                                <div className="h-8 bg-gray-200 rounded w-1/2 mx-auto mb-4"></div>
                                <div className="h-3 bg-gray-200 rounded w-5/6 mx-auto"></div>
                            </div>
                            <p className="text-sm text-gray-600 mt-4">Loading your climate impact data...</p>
                        </div>
                    ) : !hasImpactData ? (
                        // Empty State
                        <div className="p-6 sm:p-8 bg-white border-2 border-dashed border-gray-300 rounded-xl text-center shadow-sm">
                            <div className="w-16 h-16 bg-primary-light rounded-full flex items-center justify-center mx-auto mb-4">
                                <BoltIcon className="w-8 h-8 text-primary" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800 mb-2 font-heading">Start Your Climate Journey</h3>
                            <p className="text-sm text-gray-600 mb-4">
                                List your first product to begin tracking your environmental impact and generating revenue.
                            </p>
                            <p className="text-xs text-gray-500 mb-6">
                                Every kg prevented = 2.5kg CO₂e saved (equivalent to {calculateEnvironmentalEquivalents(2.5).milesDriven} miles not driven)
                            </p>
                            <button
                                className="bg-primary hover:bg-primary-light text-white py-3 px-6 rounded-lg text-sm font-semibold transition-colors shadow-md font-accent"
                                onClick={handleOpenRequestModal}
                            >
                                List Your First Product
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* FOCUSED REVENUE METRICS CARDS */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-10">
                                {/* Revenue Card - FOCUSED ON OFFERED PRICES */}
                                <EnhancedMetricCard
                                    title="Revenue Generated"
                                    mainValue={formatCurrency(impactMetrics.revenueGenerated)}
                                    subtitle="Pending Revenue"
                                    subvalue={formatCurrency(impactMetrics.pendingRevenue)}
                                    additionalInfo={
                                        <>
                                            <div className="flex justify-between">
                                                <span>Active Listings Value</span>
                                                <span className="font-semibold text-secondary">
                                                    {formatCurrency(impactMetrics.potentialRevenue)}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Total Inventory Value</span>
                                                <span className="font-semibold text-primary">
                                                    {formatCurrency(impactMetrics.totalInventoryValue)}
                                                </span>
                                            </div>
                                        </>
                                    }
                                    borderColor="border-primary-light"
                                    textColor="text-primary"
                                />

                                {/* Waste Prevention */}
                                <EnhancedMetricCard
                                    title="Waste Prevention"
                                    mainValue={`${formatMetricValue(impactMetrics.actualWastePreventedKg)} KG`}
                                    subtitle="Potential Prevention"
                                    subvalue={`${formatMetricValue(impactMetrics.potentialWastePreventedKg)} KG`}
                                    additionalInfo={
                                        <>
                                            <div className="flex justify-between">
                                                <span>Active Listings</span>
                                                <span className="font-semibold">{formatMetricValue(impactMetrics.totalActiveListings)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Expiring Soon</span>
                                                <span className="font-semibold text-accent">
                                                    {formatMetricValue(impactMetrics.expiringSoonCount)}
                                                </span>
                                            </div>
                                        </>
                                    }
                                    borderColor="border-secondary-light"
                                    textColor="text-secondary"
                                />

                                {/* Emissions Reduction */}
                                <EnhancedMetricCard
                                    title="Emissions Reduction"
                                    mainValue={`${formatMetricValue(impactMetrics.actualPollutionPreventedCo2)} kg CO₂e`}
                                    subtitle="Potential Reduction"
                                    subvalue={`${formatMetricValue(impactMetrics.potentialPollutionPreventedCo2)} kg CO₂e`}
                                    climateContext={`Equivalent to ${actualEquivalents.milesDriven} miles not driven`}
                                    additionalInfo={
                                        <>
                                            <div className="flex justify-between">
                                                <span>Total Listings</span>
                                                <span className="font-semibold">{formatMetricValue(impactMetrics.totalListingsCount)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span>Listings with Offers</span>
                                                <span className="font-semibold text-primary">
                                                    {formatMetricValue(impactMetrics.matchedListingsCount)}
                                                </span>
                                            </div>
                                        </>
                                    }
                                    borderColor="border-primary-light"
                                    textColor="text-primary"
                                />
                            </div>

                            {/* Performance Summary */}
                            <div className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 shadow-sm mb-8">
                                <h3 className="font-semibold text-gray-800 mb-4 font-subheading">📊 Performance Summary</h3>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                                    <div>
                                        <p className="text-2xl font-bold text-primary font-heading">{impactMetrics.totalDeliveredMatches}</p>
                                        <p className="text-xs text-gray-600">Delivered Orders</p>
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-accent font-heading">{impactMetrics.totalPendingMatches}</p>
                                        <p className="text-xs text-gray-600">Pending Orders</p>
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-secondary font-heading">{impactMetrics.totalActiveListings}</p>
                                        <p className="text-xs text-gray-600">Active Listings</p>
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-primary font-heading">{impactMetrics.matchedListingsCount}</p>
                                        <p className="text-xs text-gray-600">Listings with Offers</p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {/* AI RECOMMENDATIONS SECTION */}
                    {aiRecommendations.length > 0 && (
                        <div className="mb-8 sm:mb-10">
                            <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-4 sm:mb-6 border-b pb-3 font-heading">
                                Actionable Insights
                            </h2>
                            <div className="space-y-4">
                                {aiRecommendations.map((rec) => (
                                    <RecommendationCard
                                        key={rec.id}
                                        message={rec.message}
                                        linkTo={rec.linkTo}
                                        actionText={rec.actionText}
                                        icon={rec.icon}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* VERSION INFO FOOTER */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-12 sm:mt-16 pt-6 border-t border-gray-200 text-center items-center justify-center">
                    <p className="text-xs text-gray-500 font-medium">
                        Climate Impact Dashboard - Early Access
                    </p>
                    <p className="text-xs text-gray-400 hidden sm:block">|</p>
                    <p className="text-xs text-gray-500 font-medium">
                        Version: {APP_VERSION} (MVP)
                    </p>
                    <p className="text-xs text-gray-400 hidden sm:block">|</p>
                    <p className="text-xs text-gray-500">
                        Making agriculture sustainable, one listing at a time 🌾
                    </p>
                </div>
            </div>

            {/* Add Product Modal */}
            {isRequestModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-y-auto">
                        <button
                            onClick={handleCloseRequestModal}
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-colors z-10"
                            aria-label="Close modal"
                        >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <AddProduct
                            token={token}
                            user={user}
                            onClose={handleCloseRequestModal}
                            onSuccess={() => {
                                handleCloseRequestModal();
                                window.location.reload();
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;